import { useEffect, useMemo, useState } from "react";
import type { MapObject, ObjectLocalized } from "@fortex/api";
import type { CatalogueEntry } from "../../catalogue/types";
import { walk } from "../../catalogue/types";
import { pairText } from "../../geo/geodesy";
import { useCatalogue, useLabels, useMapCore } from "../hooks";
import { Icon } from "../icons";
import { copyText, machineCoordinates, objectTitle } from "../fieldValues";
import { breadcrumb } from "../layerTreeModel";
import { Button } from "../primitives/Button";
import { Chip } from "../primitives/Chip";
import { Skeleton } from "../primitives/Skeleton";
import { colourOf, Swatch } from "./Swatch";

export type ObjectCardProps = {
  object: MapObject;
  detail: ObjectLocalized | null;
  loading?: boolean;
  error?: string | null;
  onClose?: (() => void) | null;
  compact?: boolean;
};

export function isObjectDetail(value: unknown): value is ObjectLocalized {
  return typeof value === "object" && value !== null && "id" in value && "fileGroups" in value && typeof (value as { name?: unknown }).name === "string";
}

export function entryForType(entries: CatalogueEntry[], typeIdName: string | null): CatalogueEntry | null {
  if (typeIdName === null) return null;

  for (const entry of walk(entries)) {
    if (entry.kind !== "geoobject") continue;
    if (entry.source?.typeIdName === typeIdName || entry.source?.typeIdNames.includes(typeIdName) || entry.idName === typeIdName) return entry;
  }

  return null;
}

const copiedDelayMs = 1500;

function usePictureUrl(fileId: string | null, enabled: boolean): string | null {
  const core = useMapCore();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || fileId === null) {
      setUrl(null);
      return;
    }

    let disposed = false;

    core.api
      .blobUrl(core.api.fileUrl(fileId))
      .then(result => {
        if (!disposed) setUrl(result);
      })
      .catch(() => {
        if (!disposed) setUrl(null);
      });

    return () => {
      disposed = true;
    };
  }, [core, fileId, enabled]);

  return url;
}

export function ObjectCard({ object, detail, loading = false, error = null, onClose = null, compact = false }: ObjectCardProps) {
  const core = useMapCore();
  const labels = useLabels();
  const catalogue = useCatalogue();
  const [copied, setCopied] = useState(false);
  const entry = useMemo(() => (catalogue === null ? null : entryForType(catalogue.layers, object.typeIdName)), [catalogue, object.typeIdName]);
  const colour = (object.typeIdName !== null ? core.layerColor(object.typeIdName) : null) ?? colourOf(entry?.style ?? null, "var(--fxmap-accent)");
  const pictures = core.options.info.pictures;
  const pictureId = detail?.firstPictureId ?? null;
  const pictureUrl = usePictureUrl(pictureId, pictures);
  const address = detail?.address?.trim() ?? "";
  const description = detail?.shortDesc?.trim() ?? "";
  const actions = core.options.info.onObjectActions?.(object) ?? [];
  const pair = pairText([object.lat, object.lon]);

  useEffect(() => {
    if (!copied) return;

    const handle = setTimeout(() => setCopied(false), copiedDelayMs);

    return () => clearTimeout(handle);
  }, [copied]);

  return (
    <article className={["fxmap-object-card", compact ? "fxmap-object-card-compact" : ""].filter(Boolean).join(" ")} data-fxmap="object-card" aria-label={objectTitle(object)} style={{ "--fxmap-type-colour": colour } as Record<string, string>}>
      <span className="fxmap-object-stripe" aria-hidden="true" />
      <header className="fxmap-object-head">
        <Swatch entry={entry} color={colour} className="fxmap-object-swatch" />
        <div className="fxmap-object-titles">
          <h2 className="fxmap-object-name fxmap-display">{objectTitle(object)}</h2>
          {entry !== null || object.typeName ? (
            <Chip tone="neutral" className="fxmap-object-type" title={entry !== null ? breadcrumb(entry) : undefined}>
              {entry !== null ? breadcrumb(entry) : object.typeName}
            </Chip>
          ) : null}
        </div>
        {onClose !== null ? <Button kind="icon" size="sm" icon="close" label={labels.close} onClick={onClose} action="close" /> : null}
      </header>
      {pictures && (loading || pictureId !== null) ? <div className="fxmap-object-picture">{pictureUrl === null ? <Skeleton shape="block" height={120} /> : <img src={pictureUrl} alt={labels.picture} loading="lazy" />}</div> : null}
      <div className="fxmap-object-details">
        {loading ? <Skeleton lines={2} /> : null}
        {!loading && error !== null ? (
          <p className="fxmap-object-error" role="alert">
            {error}
          </p>
        ) : null}
        {!loading && address.length > 0 ? (
          <div className="fxmap-object-row">
            <span className="fxmap-object-row-label fxmap-muted">{labels.address}</span>
            <span className="fxmap-object-row-value">{address}</span>
          </div>
        ) : null}
        {!loading && description.length > 0 ? <p className="fxmap-object-description">{description}</p> : null}
        {!loading && detail?.externalUrl ? (
          <a className="fxmap-object-link" href={detail.externalUrl} target="_blank" rel="noreferrer">
            <Icon name="external" />
            <span>{detail.externalUrlName && detail.externalUrlName.trim().length > 0 ? detail.externalUrlName : detail.externalUrl}</span>
          </a>
        ) : null}
      </div>
      <div className="fxmap-object-coords">
        <span className="fxmap-object-coords-value fxmap-mono" data-fxmap="object-coordinates">
          {copied ? labels.copied : pair}
        </span>
        <Button kind="icon" size="sm" icon="copy" label={labels.copy} action="copy" onClick={() => void copyText(machineCoordinates(object.lat, object.lon)).then(done => setCopied(done))} />
      </div>
      {actions.length > 0 ? (
        <div className="fxmap-object-actions">
          {actions.map(action => (
            <Button key={action.id} kind={action.primary ? "primary" : "ghost"} size="sm" label={action.label} data-action={action.id} onClick={() => action.run(object)} />
          ))}
        </div>
      ) : null}
    </article>
  );
}
